import { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/css';
import { CloseOutlined } from '@ant-design/icons';
import { Divider, message, Popover, Space, Tooltip } from 'antd';
import { SystemLanguage } from 'hooks/business/system/useSystem';
import { useTriggerMapValue } from 'hooks/business/useTrigger';
import { create } from 'services/api/trigger';
import StatusButton from 'components/StatusButton';
import { updateConversation } from 'services/api/conversation.convList';
import { useTranslate } from '@tolgee/react';
import dayjs from 'lib/dayjs';
import useTipTrigger from './tip';
import { useChatConversationContext } from '../../contexts/ChatConversationContext';

const Container = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: 40px;
  padding: 0 8px;
  background: #fffaf0;
`;

const Text = styled.div`
  flex: 1 0 0;
  padding-right: 20px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  > span {
    padding: 0 5px;
  }
  .${({ theme }) => theme.getAntdPrefixedClassName('divider')} {
    border-left-color: rgba(0, 0, 0, 0.3);
  }
`;

const PopoverContent = styled.div`
  position: relative;
  width: 265px;
  margin: -12px -16px;
  padding: 15px 10px;
  background: #5287ff;
  color: #fff;
  > ul {
    margin: 0;
    padding: 0 5px 0 15px;
    dt {
      margin-bottom: 5px;
      font-size: 14px;
    }
    dd {
      font-size: 12px;
    }
  }
  .anticon {
    position: absolute;
    top: 10px;
    right: 10px;
  }
`;

const overlayClassName = css`
  .ant-popover-arrow-content {
    background: #5287ff;
  }
`;

export function getRemindFromHint(hint: string, systemLanguage: SystemLanguage) {
  try {
    const { [systemLanguage]: remind } = JSON.parse(hint);

    return remind;
  } catch (err) {
    return '';
  }
}

function Remind() {
  const { t } = useTranslate();

  const { currentConversation: conversation } = useChatConversationContext();

  const { REMIND } = useTriggerMapValue();

  const [visible, setVisible] = useState(!REMIND);

  const tipTrigger = useTipTrigger();

  useEffect(() => {
    if (!REMIND) {
      setVisible(!REMIND);
    }
  }, [REMIND]);

  if (!conversation) {
    return null;
  }

  const {
    talkId,
    assistantId,
    controlStatus,
    supervisePower,
    remindTriggerContent = [],
  } = conversation;

  if (remindTriggerContent.length === 0 || (!controlStatus && !supervisePower)) {
    return null;
  }

  const remindList = remindTriggerContent.map((trig) => {
    const { scene, waitReply, timestamp, condition = '' } = trig;

    return { timestamp, text: tipTrigger(scene, condition, waitReply) };
  });

  const onResolve = () => {
    return updateConversation({
      userId: assistantId,
      talkId: [talkId],
      solveRemind: true,
    })
      .then(() => {
        message.success(t('scene.chatWorkspace.currentConversation.status.buyerIssueResolved'));
      })
      .catch(() => {
        message.error(t('common.feedback.networkErrorRetry'));
      });
  };
  const onIgnoreRemind = () => {
    return updateConversation({
      userId: assistantId,
      talkId: [talkId],
      ignoreRemind: true,
    })
      .then(() => {
        message.success(t('common.feedback.saveSuccess'));
      })
      .catch(() => {
        message.error(t('common.feedback.networkErrorRetry'));
      });
  };

  const button = (
    <Space>
      <StatusButton size="small" onClick={onIgnoreRemind}>
        {t('scene.chatWorkspace.currentConversation.review.noActionNeeded')}
      </StatusButton>
      <StatusButton type="primary" size="small" onClick={onResolve}>
        {t('scene.chatWorkspace.currentConversation.action.markResolved')}
      </StatusButton>
    </Space>
  );

  return (
    <Container>
      <Tooltip
        overlayInnerStyle={{ width: '520px' }}
        title={remindList.map(({ text, timestamp }, index) => (
          <div>
            {index + 1}: {dayjs(Number(timestamp)).format('YY-MM-DD HH:mm')} {text}
          </div>
        ))}
      >
        <Text>
          {remindList.map(({ text }, index) => {
            if (index === 0) {
              return <span>{text}</span>;
            }
            return (
              <>
                <Divider type="vertical" />
                <span>{text}</span>
              </>
            );
          })}
        </Text>
      </Tooltip>

      {REMIND ? (
        button
      ) : (
        <Popover
          visible={visible}
          placement="bottomRight"
          getPopupContainer={(trigger) => trigger}
          overlayClassName={overlayClassName}
          content={
            <PopoverContent>
              <ul>
                <li>
                  <dt>
                    {t('scene.chatWorkspace.currentConversation.reminder.strongReminderTitle')}
                  </dt>
                  <dd>
                    {t(
                      'scene.chatWorkspace.currentConversation.reminder.strongReminderDescription',
                    )}
                    {t('scene.chatWorkspace.currentConversation.reminder.strongReminderActionHint')}
                  </dd>
                </li>
              </ul>
              <CloseOutlined
                onClick={() => {
                  setVisible(false);
                  create({
                    trigger: 'REMIND',
                  }).catch(() => {});
                }}
              />
            </PopoverContent>
          }
        >
          {button}
        </Popover>
      )}
    </Container>
  );
}

export default Remind;
